import { Component } from '@theme/component';

/** @typedef {{id:string, sku:string, label:string, pieces:number, price:number, available:boolean}} Pack */
/** @typedef {{id:string, group:string, title:string, size:string, model:string, url:string, image:string, variants:Pack[]}} SizeProduct */
/**
 * Narrows the published size catalog and links directly to the selected pack.
 * @extends {Component<{catalog:HTMLScriptElement, labels:HTMLScriptElement, controls:HTMLDivElement, material:HTMLSelectElement, size:HTMLSelectElement, pack:HTMLSelectElement, search:HTMLInputElement, reset:HTMLButtonElement, cards:HTMLDivElement, count:HTMLParagraphElement, empty:HTMLParagraphElement, more:HTMLButtonElement}>}
 */
class QSSizeFinder extends Component {
  requiredRefs = ['catalog', 'labels', 'controls', 'material', 'size', 'pack', 'search', 'reset', 'cards', 'count', 'empty', 'more'];
  /** @type {SizeProduct[]} */
  #catalog = [];
  #labels = {};
  #controller;
  #money;
  #unitMoney;
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      this.#catalog = JSON.parse(this.refs.catalog.textContent || '[]');
      this.#labels = JSON.parse(this.refs.labels.textContent || '{}');
      if (!Array.isArray(this.#catalog) || !this.#catalog.length) return;
      const groups = ['Poly', 'Kraft', 'Pouches'].map((group) => this.#catalog.filter((item) => item.group === group));
      this.#catalog = [];
      while (groups.some((group) => group.length)) {
        for (const group of groups) {
          const product = group.shift();
          if (product) this.#catalog.push(product);
        }
      }
      const currency = this.dataset.currency || 'USD';
      const locale = this.dataset.locale || 'en-US';
      this.#money = new Intl.NumberFormat(locale, { style: 'currency', currency });
      this.#unitMoney = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 3, maximumFractionDigits: 3 });
    } catch {
      return; // The semantic browse links remain usable without enhancement.
    }
    this.refs.material.addEventListener('change', () => {
      this.#expanded = false;
      this.#options();
      this.#render();
    }, { signal });
    const filter = () => { this.#expanded = false; this.#render(); };
    this.refs.size.addEventListener('change', filter, { signal });
    this.refs.pack.addEventListener('change', filter, { signal });
    this.refs.search.addEventListener('input', filter, { signal });
    this.refs.more.addEventListener('click', () => {
      this.#expanded = true;
      this.#render();
      this.refs.cards.querySelector('article:nth-child(7) a')?.focus();
    }, { signal });
    this.refs.reset.addEventListener('click', () => {
      this.refs.material.value = '';
      this.refs.size.value = '';
      this.refs.pack.value = '';
      this.refs.search.value = '';
      this.#expanded = false;
      this.#options();
      this.#render();
      this.refs.material.focus();
    }, { signal });
    this.refs.cards.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLSelectElement)) return;
      const card = event.target.closest('[data-product]');
      const product = this.#catalog.find((item) => item.id === card?.dataset.product);
      const pack = product?.variants.find((item) => item.id === event.target.value);
      if (card && product && pack) this.#updateCard(card, product, pack);
    }, { signal });
    window.addEventListener('popstate', () => { this.#readUrl(); this.#render(); }, { signal });
    this.#readUrl();
    this.refs.controls.hidden = false;
    this.refs.cards.hidden = false;
    this.#render();
  }

  #normalize(value) {
    return String(value || '').toLowerCase().replace(/[×✕]/g, 'x').replace(/[^a-z0-9.]/g, '');
  }

  #readUrl() {
    const params = new URL(window.location.href).searchParams;
    const material = params.get('material') || '';
    this.refs.material.value = ['Poly', 'Kraft', 'Pouches'].find((group) => group.toLowerCase() === material.toLowerCase()) || '';
    this.#options();
    for (const key of ['size', 'pack']) {
      const select = this.refs[key];
      const value = params.get(key) || '';
      select.value = [...select.options].some((option) => option.value === value) ? value : '';
    }
    this.refs.search.value = params.get('q') || '';
    this.#expanded = false;
  }

  #writeUrl() {
    if (window.Shopify?.designMode) return;
    const url = new URL(window.location.href);
    for (const [key, value] of [['material', this.refs.material.value], ['size', this.refs.size.value], ['pack', this.refs.pack.value], ['q', this.refs.search.value.trim()]]) {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
  }

  #options() {
    const products = this.#catalog.filter((item) => !this.refs.material.value || item.group === this.refs.material.value);
    const sizes = [...new Set(products.map((item) => item.size))].sort((a, b) => {
      const [aw, ah] = a.split('x').map(Number);
      const [bw, bh] = b.split('x').map(Number);
      return aw - bw || ah - bh;
    });
    const packs = [...new Set(products.flatMap((item) => item.variants.map((pack) => pack.pieces)))].sort((a, b) => a - b);
    this.#setOptions(this.refs.size, sizes.map((size) => [size, `${size.replace('x', ' × ')} in`]), this.#labels.allSizes);
    this.#setOptions(this.refs.pack, packs.map((pieces) => [String(pieces), `${pieces} ${this.#labels.pieces}`]), this.#labels.allPacks);
  }

  #setOptions(select, values, placeholder) {
    const previous = select.value;
    select.replaceChildren(new Option(placeholder, ''));
    for (const [value, label] of values) select.add(new Option(label, value));
    select.value = values.some(([value]) => value === previous) ? previous : '';
  }

  #render() {
    const rawQuery = this.refs.search.value.trim().replace(/[;,]/g, ' ');
    const query = this.#normalize(rawQuery);
    const exactSku = !!query && this.#catalog.some((product) => product.variants.some((pack) => this.#normalize(pack.sku) === query));
    const dimensions = rawQuery.match(/\b(\d+(?:\.\d+)?)\s*[x×✕]\s*(\d+(?:\.\d+)?)(?:\s*(?:inches|inch|in)\b|\s*["”])?(?=\s|$)/i);
    const exactSize = dimensions ? `${dimensions[1]}x${dimensions[2]}` : '';
    const bagMatch = rawQuery.match(/(?:^|\s)#\s*(\d+)(?=\s|$)/);
    const bagNumber = bagMatch?.[1];
    let remainder = rawQuery;
    if (dimensions) remainder = remainder.replace(dimensions[0], ' ');
    if (bagMatch) remainder = remainder.replace(bagMatch[0], ' ');
    const packMatch = remainder.match(/\b(\d+)\s*(?:pcs?|pieces?)\b/i);
    if (packMatch) remainder = remainder.replace(packMatch[0], ' ');
    const terms = remainder.split(/\s+/).map((term) => this.#normalize(term)).filter((term) => term && !['in', 'inch', 'inches'].includes(term));
    const bareQuantity = terms.find((term) => /^\d+$/.test(term));
    const quantity = packMatch ? Number(packMatch[1]) : (bareQuantity ? Number(bareQuantity) : null);
    const keywords = terms.filter((term) => term !== bareQuantity);
    const fragment = document.createDocumentFragment();
    let count = 0;
    for (const product of this.#catalog) {
      if (this.refs.material.value && product.group !== this.refs.material.value) continue;
      if (this.refs.size.value && product.size !== this.refs.size.value) continue;
      if (exactSize && this.#normalize(product.size) !== exactSize) continue;
      if (bagNumber !== undefined && product.model.match(/#(\d+)/)?.[1] !== bagNumber) continue;
      const productText = `${product.title} ${product.model} ${product.size} ${product.group} ${this.#labels[`${product.group}Terms`] || ''}`;
      const packs = product.variants.filter((pack) =>
        (!this.refs.pack.value || pack.pieces === Number(this.refs.pack.value)) &&
        (exactSku ? this.#normalize(pack.sku) === query :
          ((quantity === null || pack.pieces === quantity) && keywords.every((term) => this.#normalize(`${productText} ${pack.sku} ${pack.label}`).includes(term))))
      );
      if (!packs.length) continue;
      if (this.#expanded || count < 6) fragment.append(this.#card(product, packs));
      count++;
    }
    this.refs.cards.replaceChildren(fragment);
    this.refs.count.textContent = count === 1 ? this.#labels.singleResult : this.#labels.results.replace('[count]', String(count));
    this.refs.more.hidden = this.#expanded || count <= 6;
    this.refs.more.textContent = this.#labels.showMore.replace('[count]', String(count));
    this.refs.empty.hidden = count > 0;
    this.#writeUrl();
  }

  #card(product, packs) {
    const card = document.createElement('article');
    card.className = 'qs-finder-card';
    card.dataset.product = product.id;
    const header = document.createElement('div');
    header.className = 'qs-finder-card-header';
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.width = 64;
      image.height = 64;
      image.loading = 'lazy';
      header.append(image);
    }
    const heading = document.createElement('h3');
    heading.textContent = product.title;
    header.append(heading);
    const field = document.createElement('div');
    field.className = 'qs-field';
    const label = document.createElement('label');
    const select = document.createElement('select');
    select.id = `${this.refs.material.id}-pack-${product.id}`;
    label.htmlFor = select.id;
    label.textContent = this.#labels.pack;
    select.setAttribute('aria-label', `${this.#labels.pack}: ${product.title}`);
    for (const pack of packs) select.add(new Option(`${pack.label} — ${this.#money.format(pack.price / 100)}${pack.available === false ? ` (${this.#labels.soldOut})` : ''}`, pack.id));
    field.append(label, select);
    const price = document.createElement('p');
    price.className = 'qs-finder-price';
    const priceLabel = document.createElement('span');
    priceLabel.textContent = this.#labels.price;
    const amount = document.createElement('strong');
    amount.dataset.amount = '';
    price.append(priceLabel, amount);
    const unit = document.createElement('p');
    unit.className = 'qs-finder-unit';
    unit.dataset.unit = '';
    const sku = document.createElement('p');
    sku.className = 'qs-finder-sku';
    sku.dataset.sku = '';
    const link = document.createElement('a');
    link.className = 'qs-btn qs-btn-solid';
    link.textContent = this.#labels.view;
    link.dataset.view = '';
    card.append(header, field, price, unit, sku, link);
    this.#updateCard(card, product, packs[0]);
    return card;
  }

  #updateCard(card, product, pack) {
    card.querySelector('[data-amount]').textContent = this.#money.format(pack.price / 100);
    card.querySelector('[data-unit]').textContent = `${this.#labels.unit}: ~${this.#unitMoney.format(pack.price / 100 / pack.pieces)}`;
    card.querySelector('[data-sku]').textContent = `SKU: ${pack.sku}`;
    const link = card.querySelector('[data-view]');
    const url = new URL(product.url, window.location.origin);
    url.searchParams.set('variant', pack.id);
    link.href = url.pathname + url.search;
    link.setAttribute('aria-label', `${this.#labels.view}: ${product.title}, ${pack.label}`);
  }
}

if (!customElements.get('qs-size-finder')) customElements.define('qs-size-finder', QSSizeFinder);
